/**
 * Client for the per-object survey write API, `api/SurveyBuilder`.
 *
 * Every route in the API is here, one function each. It talks to the shared axios instance
 * directly rather than through `httpService`, for three reasons this API needs and that wrapper
 * cannot give:
 *
 *   - `If-Match` has to be set per request (`httpService.put`/`delete` take no config at all, and
 *     `post` passes its `config` argument straight into `headers`).
 *   - the response envelope carries `Data.SurveyVersion`, which has to be kept even on the calls
 *     whose saved object we ignore.
 *   - a 409 is a meaningful answer here, not a generic failure, so it must reach the caller with
 *     the server's own message intact.
 *
 * Reads are untouched: the builder still loads a survey through `Survey/GetPreviewData`.
 */

import https from "../../../../base/utils/http";

const BASE = "SurveyBuilder";

/** `afterId` for "put this first". Omitting `afterId` instead means "put this last". */
export const FIRST_POSITION_ID = "00000000-0000-0000-0000-000000000000";

/** The server's cap. A batch over this is refused whole, so it is checked before sending. */
export const MAX_BATCH_OPERATIONS = 500;

const GENERIC_ERROR = "Something went wrong! Please try again later.";

/**
 * The envelope is documented as PascalCase (Newtonsoft) while every response this app reads today
 * arrives camelCase, so each field is read under both spellings rather than betting on one. A
 * missed `SurveyVersion` would silently disable optimistic locking - exactly the failure this
 * whole mechanism exists to prevent - so it is worth the few extra lookups.
 */
function pick(source, ...names) {
   if (!source) return undefined;

   for (const name of names) {
      if (source[name] !== undefined) return source[name];
   }

   return undefined;
}

/** Error carrying the envelope, so callers can tell a stale version from a real failure. */
export class SurveyBuilderError extends Error {
   constructor(message, { statusCode = null, envelope = null } = {}) {
      super(message || GENERIC_ERROR);
      this.name = "SurveyBuilderError";
      this.statusCode = statusCode;
      this.envelope = envelope;
   }

   /**
    * 409 covers three cases that all mean "do not retry blindly": somebody else's edit landed
    * first, an id that already exists, and a choice that answers already point at. All three are
    * resolved by re-reading the survey and showing the server's message, so they share a flag.
    */
   get isConflict() {
      return this.statusCode === 409;
   }
}

/** First human-readable string an error-shaped payload carries. */
function readErrorMessage(payload) {
   if (!payload) return null;

   const errors = pick(payload, "Errors", "errors");
   if (Array.isArray(errors) && errors.length) {
      return errors.filter(Boolean).join(" ");
   }

   return pick(payload, "Message", "message") || pick(payload, "Title", "title") || null;
}

/** Normalises an axios rejection, or an unsuccessful 200 envelope, into a `SurveyBuilderError`. */
export function toSurveyBuilderError(error) {
   if (error instanceof SurveyBuilderError) {
      return error;
   }

   const envelope = error?.response?.data ?? error;

   return new SurveyBuilderError(readErrorMessage(envelope) || error?.message || GENERIC_ERROR, {
      statusCode: error?.response?.status ?? pick(envelope, "StatusCode", "statusCode") ?? null,
      envelope
   });
}

/**
 * Unwraps a `BaseResponse`, throwing when it reports failure.
 *
 * @returns {{surveyId: string|null, surveyVersion: number|null, item: Object|null}}
 */
export function readBuilderEnvelope(envelope, { statusCode = null } = {}) {
   // This API names the flag `succeded` - the typo is the server's - where the rest of the app's
   // endpoints name it `isSuccess`. Both are read, misspelling included, because the flag is the
   // only thing that catches a 200 whose body reports a failure; going by the header status alone
   // would read such a response as a saved survey.
   const isSuccess = pick(
      envelope,
      "IsSuccess",
      "isSuccess",
      "Succeded",
      "succeded",
      "Succeeded",
      "succeeded"
   );
   const bodyStatus = pick(envelope, "StatusCode", "statusCode");

   if (!envelope || isSuccess === false || (typeof bodyStatus === "number" && bodyStatus >= 400)) {
      throw new SurveyBuilderError(readErrorMessage(envelope), {
         statusCode: bodyStatus ?? statusCode,
         envelope
      });
   }

   const data = pick(envelope, "Data", "data") ?? {};

   return {
      surveyId: pick(data, "SurveyId", "surveyId") ?? null,
      surveyVersion: pick(data, "SurveyVersion", "surveyVersion") ?? null,
      item: pick(data, "Item", "item") ?? null
   };
}

/**
 * One call against the API.
 *
 * `version` is the last `SurveyVersion` seen. Sent as `If-Match`, it turns a write that would
 * overwrite somebody else's edit into a 409 instead of a silent win. It is deliberately omitted
 * when unknown rather than guessed: the read endpoints do not report a version, so the first write
 * after a page load has nothing to send and goes through unconditionally.
 */
function request({ method, path, data, params, version, headers }) {
   return https
      .request({
         method,
         url: `${BASE}/${path}`,
         ...(data !== undefined && { data }),
         ...(params && { params }),
         headers: {
            ...(version !== null && version !== undefined && { "If-Match": String(version) }),
            ...headers
         }
      })
      .then(
         (response) => readBuilderEnvelope(response?.data, { statusCode: response?.status }),
         (error) => {
            throw toSurveyBuilderError(error);
         }
      );
}

// --------------------------------------------------------------------------------- sections

/**
 * Creates a section.
 *
 * `id` is generated here rather than by the server - that is what makes a retry after a timeout
 * safe, and what lets the new section be rendered and referenced before the response lands.
 * Exactly one of `surveyId` / `sectionId`, or neither for the survey's own top level.
 */
export function createSection({ id, surveyId, sectionId, languageId, afterId, ...rest }, options) {
   return request({
      method: "post",
      path: "Sections",
      data: { id, surveyId, sectionId, languageId, afterId, ...rest },
      ...options
   });
}

/** Partial update - anything left out keeps its current value. */
export function updateSection(id, data, options) {
   return request({ method: "put", path: `Sections/${id}`, data, ...options });
}

/** The section's wording in one language. `AddTranslation`'s per-section entry, one call. */
export function setSectionLabel(id, languageId, { name, description }, options) {
   return request({
      method: "put",
      path: `Sections/${id}/Labels/${languageId}`,
      data: { name, description },
      ...options
   });
}

/** Moves a section. Exactly one of `surveyId` / `sectionId`, or neither to stay where it is. */
export function moveSection(id, { surveyId, sectionId, afterId }, options) {
   return request({
      method: "post",
      path: `Sections/${id}/Move`,
      data: { surveyId, sectionId, afterId },
      ...options
   });
}

export function deleteSection(id, options) {
   return request({ method: "delete", path: `Sections/${id}`, ...options });
}

// -------------------------------------------------------------------------------- questions

/** Creates a question, optionally with its choices in the same call. */
export function createQuestion(
   { id, surveyId, sectionId, languageId, afterId, ...rest },
   options
) {
   return request({
      method: "post",
      path: "Questions",
      data: { id, surveyId, sectionId, languageId, afterId, ...rest },
      ...options
   });
}

export function updateQuestion(id, data, options) {
   return request({ method: "put", path: `Questions/${id}`, data, ...options });
}

export function moveQuestion(id, { surveyId, sectionId, afterId }, options) {
   return request({
      method: "post",
      path: `Questions/${id}/Move`,
      data: { surveyId, sectionId, afterId },
      ...options
   });
}

/**
 * Copies a question server-side, under an id generated here.
 *
 * Worth preferring over rebuilding a copy from the local tree: the server copies everything the
 * question carries, including what the editor does not model - a range, matrix columns, choice
 * filters - which a client-side copy would silently lose.
 */
export function duplicateQuestion(id, newId, options) {
   return request({
      method: "post",
      path: `Questions/${id}/Duplicate`,
      params: { newId },
      ...options
   });
}

/**
 * Deletes a question - or, when answers already point at it, hides it. Either way the call
 * succeeds and the question stops coming back from reads.
 */
export function deleteQuestion(id, options) {
   return request({ method: "delete", path: `Questions/${id}`, ...options });
}

/** The question's wording in one language. */
export function setQuestionLabel(id, languageId, text, options) {
   return request({
      method: "put",
      path: `Questions/${id}/Labels/${languageId}`,
      data: { text },
      ...options
   });
}

export function setQuestionHint(id, languageId, text, options) {
   return request({
      method: "put",
      path: `Questions/${id}/Hints/${languageId}`,
      data: { text },
      ...options
   });
}

export function deleteQuestionHint(id, languageId, options) {
   return request({ method: "delete", path: `Questions/${id}/Hints/${languageId}`, ...options });
}

export function setQuestionConstraintMessage(id, languageId, text, options) {
   return request({
      method: "put",
      path: `Questions/${id}/ConstraintMessages/${languageId}`,
      data: { text },
      ...options
   });
}

export function deleteQuestionConstraintMessage(id, languageId, options) {
   return request({
      method: "delete",
      path: `Questions/${id}/ConstraintMessages/${languageId}`,
      ...options
   });
}

/**
 * Replaces a question's image.
 *
 * The only multipart call in the API - everything else is JSON - so it is also the reason the
 * survey save itself no longer has to be multipart.
 */
export function uploadQuestionImage(questionId, file, options) {
   const formData = new FormData();
   formData.append("Image", file);

   return request({
      method: "put",
      path: `Questions/${questionId}/Image`,
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
      ...options
   });
}

export function deleteQuestionImage(questionId, options) {
   return request({ method: "delete", path: `Questions/${questionId}/Image`, ...options });
}

// ---------------------------------------------------------------------------------- choices

/** Adds choices to a question, in the order given, under ids generated here. */
export function addChoices(questionId, { languageId, choices }, options) {
   return request({
      method: "post",
      path: `Questions/${questionId}/Choices`,
      data: { languageId, choices },
      ...options
   });
}

/** The whole list, in the order it should end up in. */
export function reorderChoices(questionId, ids, options) {
   return request({
      method: "post",
      path: `Questions/${questionId}/Choices/Reorder`,
      data: { ids },
      ...options
   });
}

/**
 * Unlinks a choice from one question, and deletes the row only if nothing else points at it - a
 * choice list can be shared. Refused with 409 when answers already picked it.
 */
export function removeChoiceFromQuestion(questionId, choiceId, options) {
   return request({
      method: "delete",
      path: `Questions/${questionId}/Choices/${choiceId}`,
      ...options
   });
}

/**
 * Edits the choice row itself, so the change shows up on every question sharing that list. The
 * editor has no concept of sharing, so nothing here tries to work around it.
 */
export function updateChoice(id, data, options) {
   return request({ method: "put", path: `Choices/${id}`, data, ...options });
}

/** The choice's wording in one language. */
export function setChoiceLabel(id, languageId, text, options) {
   return request({
      method: "put",
      path: `Choices/${id}/Labels/${languageId}`,
      data: { text },
      ...options
   });
}

// ---------------------------------------------------------------------------- second choices

/**
 * The other axis of a Matrix (277) or a Rating (276) - its columns and its scale points. The two
 * question types go through one code path on the server and one here; nothing branches on type.
 *
 * Deliberately separate from `Choices`: a second choice is its own table with its own ids, and
 * `AnswerMatrix` / `AnswerRating` point straight at those rows, which is why a delete is refused
 * with 409 once answers exist.
 */
export function addSecondChoices(questionId, { languageId, secondChoices }, options) {
   return request({
      method: "post",
      path: `Questions/${questionId}/SecondChoices`,
      data: { languageId, secondChoices },
      ...options
   });
}

/** The whole list, in the order it should end up in. */
export function reorderSecondChoices(questionId, ids, options) {
   return request({
      method: "post",
      path: `Questions/${questionId}/SecondChoices/Reorder`,
      data: { ids },
      ...options
   });
}

/** Refused with 409 when answers already point at the row. */
export function removeSecondChoiceFromQuestion(questionId, id, options) {
   return request({
      method: "delete",
      path: `Questions/${questionId}/SecondChoices/${id}`,
      ...options
   });
}

/** A language is required whenever the body carries `text`, or the server answers 400. */
export function updateSecondChoice(id, data, options) {
   return request({ method: "put", path: `SecondChoices/${id}`, data, ...options });
}

export function setSecondChoiceLabel(id, languageId, text, options) {
   return request({
      method: "put",
      path: `SecondChoices/${id}/Labels/${languageId}`,
      data: { text },
      ...options
   });
}

// ------------------------------------------------------------------------------------ range

/**
 * A Range question's bounds and labels. Partial like every other PUT, so labels can be written
 * without resending the bounds. `to < from` is a 400.
 */
export function setQuestionRange(id, range, options) {
   return request({ method: "put", path: `Questions/${id}`, data: { range }, ...options });
}

export function deleteQuestionRange(id, options) {
   return request({ method: "delete", path: `Questions/${id}/Range`, ...options });
}

// ------------------------------------------------------------------------------------ batch

/**
 * Applies a list of operations to one survey inside a single transaction: either all of them land
 * or none does.
 *
 * @param {string} surveyId
 * @param {{languageId: string, operations: Array<Object>}} body
 */
export function saveBatch(surveyId, { languageId, operations }, options = {}) {
   if (operations.length > MAX_BATCH_OPERATIONS) {
      return Promise.reject(
         new SurveyBuilderError(
            `This save is ${operations.length} changes, over the ${MAX_BATCH_OPERATIONS} a single ` +
               "request allows. Save in smaller steps."
         )
      );
   }

   return request({
      method: "post",
      path: `Surveys/${surveyId}/Batch`,
      data: { languageId, operations },
      ...options
   });
}

/**
 * Sends one operation as the single-object call it corresponds to.
 *
 * The API's own guidance is to batch anything that is more than one change and use the plain calls
 * for everything else, so a save that turns out to be a single edit - which is most of them, since
 * an author usually fixes one wording and saves - goes out as one small `PUT` rather than as a
 * transaction wrapping a list of one.
 *
 * The operation shapes are exactly the batch's, so the diff stays the single description of a
 * change and this is only a routing table.
 */
export function sendOperation(operation, { languageId, surveyId, version = null } = {}) {
   const { op, id, questionId, data = {} } = operation;
   const options = { version };
   const language = data.languageId ?? languageId;

   // Inside a batch, an operation that names no parent means the root of the survey in the route.
   // On its own there is no route to fall back on, so the survey has to be named.
   const parented =
      data.sectionId || data.surveyId ? data : { ...data, ...(surveyId && { surveyId }) };

   switch (String(op).toLowerCase()) {
      case "section.add":
         return createSection({ id, languageId: language, ...parented }, options);
      case "section.update":
         return updateSection(id, { languageId: language, ...data }, options);
      case "section.label":
         return setSectionLabel(id, language, data, options);
      case "section.move":
         return moveSection(id, data, options);
      case "section.delete":
         return deleteSection(id, options);

      case "question.add":
         return createQuestion({ id, languageId: language, ...parented }, options);
      case "question.update":
         return updateQuestion(id, { languageId: language, ...data }, options);
      case "question.label":
         return setQuestionLabel(id, language, data.text, options);
      // A batch has no "clear the hint" operation, so an empty string is how it says so there.
      // Sent on its own the API has a verb for exactly that, and it is used.
      case "question.hint":
         return data.text
            ? setQuestionHint(id, language, data.text, options)
            : deleteQuestionHint(id, language, options);
      case "question.constraintmessage":
         return data.text
            ? setQuestionConstraintMessage(id, language, data.text, options)
            : deleteQuestionConstraintMessage(id, language, options);
      case "question.move":
         return moveQuestion(id, data, options);
      case "question.delete":
         return deleteQuestion(id, options);

      case "choice.add":
         return addChoices(questionId, { languageId: language, choices: data.choices }, options);
      case "choice.update":
         return updateChoice(id, { languageId: language, ...data }, options);
      case "choice.label":
         return setChoiceLabel(id, language, data.text, options);
      case "choice.reorder":
         return reorderChoices(questionId, data.ids, options);
      case "choice.delete":
         return removeChoiceFromQuestion(questionId, id, options);

      case "secondchoice.add":
         return addSecondChoices(
            questionId,
            { languageId: language, secondChoices: data.secondChoices },
            options
         );
      case "secondchoice.update":
         return updateSecondChoice(id, { languageId: language, ...data }, options);
      case "secondchoice.label":
         return setSecondChoiceLabel(id, language, data.text, options);
      case "secondchoice.reorder":
         return reorderSecondChoices(questionId, data.ids, options);
      case "secondchoice.delete":
         return removeSecondChoiceFromQuestion(questionId, id, options);

      // A batch says "remove the range" by sending the operation with no `data`, the way
      // `question.hint` says it with an empty string. On its own there is a verb for exactly that.
      case "question.range":
         return Object.keys(data).length
            ? setQuestionRange(id, data, options)
            : deleteQuestionRange(id, options);

      default:
         return Promise.reject(new SurveyBuilderError(`Unknown operation "${op}".`));
   }
}
